<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex justify-between items-center max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">บัญชีผู้ใช้</h1>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex  items-center justify-center py-4">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
      </div>

      <!-- User Profile -->
      <div v-else-if="userData" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <div class="flex flex-col md:flex-row items-center md:items-start gap-6">
          <!-- User Details -->
          <div class="w-full">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Display Mode -->
              <div v-if="!isEditing" class="space-y-3">
                <p class="text-sm text-indigo-100 flex items-center gap-2">
                  <i class="fas fa-envelope text-indigo-100"></i> UID: {{ userData.uid || 'ไม่ระบุ' }}
                </p>
                <p class="text-sm text-indigo-100 flex items-center gap-2">
                  <i class="fas fa-envelope text-indigo-100"></i> อีเมล: {{ userData.email || 'ไม่ระบุ' }}
                </p>
                <p class="text-sm text-indigo-100 flex items-center gap-2">
                  <i class="fas fa-gem text-indigo-100"></i> แคช: {{ userData.cash }}
                </p>
                <p class="text-sm text-indigo-100 flex items-center gap-2">
                  <i class="fas fa-gamepad text-indigo-100"></i> ตัวละคร: {{ userData.characterCreated ? 'สร้างแล้ว' : 'ยังไม่ได้สร้าง' }}
                </p>
                <!-- Stop Playing Button (Only if character exists) -->
                <div v-if="userData.characterCreated" class="mt-4">
                  <button
                    @click="openStopPlayingModal"
                    class="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200 flex items-center gap-2"
                  >
                    <i class="fas fa-pause"></i> หยุดเล่น
                  </button>
                </div>
                <!-- Delete Character Button (Only if character exists) -->
                <div v-if="userData.characterCreated" class="mt-4">
                  <button
                    @click="openDeleteModal"
                    class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200 flex items-center gap-2"
                  >
                    <i class="fas fa-trash-alt"></i> ลบตัวละคร
                  </button>
                </div>
                <!-- Sign Out Button -->
                <div class="mt-4">
                  <button
                    @click="signOut"
                    :disabled="isSigningOut"
                    class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200 flex items-center gap-2"
                  >
                    <i v-if="isSigningOut" class="fas fa-spinner animate-spin"></i>
                    <i v-else class="fas fa-sign-out-alt"></i> ออกจากระบบ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No User Data -->
      <div v-else class="text-center text-indigo-200 py-6">
        <i class="fas fa-user-slash text-2xl mb-2"></i>
        <p class="text-sm">ไม่พบข้อมูลผู้ใช้ กรุณาล็อกอิน</p>
      </div>

      <!-- Stop Playing Confirmation Modal -->
      <div v-if="showStopPlayingModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันการหยุดเล่น</h2>
          <p class="text-sm text-indigo-100 mb-4">
            คุณแน่ใจหรือไม่ว่าต้องการหยุดเล่น? คุณจะไม่เสียไอเทมใดๆ แต่จะต้องรอ 3 วันเพื่อเกิดใหม่ การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
          <div class="flex justify-end gap-2">
            <button
              @click="showStopPlayingModal = false"
              class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ยกเลิก
            </button>
            <button
              @click="proceedToStopPlayingVerification"
              class="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ดำเนินการต่อ
            </button>
          </div>
        </div>
      </div>

      <!-- Stop Playing Verification Modal -->
      <div v-if="showStopPlayingVerificationModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันขั้นสุดท้าย</h2>
          <p class="text-sm text-indigo-100 mb-4">
            กรุณาพิมพ์ชื่อตัวละครของคุณ (<strong>{{ characterName }}</strong>) เพื่อยืนยันการหยุดเล่น
          </p>
          <input
            v-model="stopPlayingVerificationInput"
            type="text"
            placeholder="พิมพ์ชื่อตัวละคร"
            class="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
          <p v-if="stopPlayingVerificationError" class="text-sm text-red-400 mb-4">{{ stopPlayingVerificationError }}</p>
          <div class="flex justify-end gap-2">
            <button
              @click="showStopPlayingVerificationModal = false"
              class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ยกเลิก
            </button>
            <button
              @click="stopPlayingCharacter"
              :disabled="isStopping"
              class="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200 flex items-center gap-2"
            >
              <i v-if="isStopping" class="fas fa-spinner animate-spin"></i>
              หยุดเล่น
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันการลบตัวละคร</h2>
          <p class="text-sm text-indigo-100 mb-4">
            คุณแน่ใจหรือไม่ว่าต้องการลบตัวละคร? การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
          <div class="flex justify-end gap-2">
            <button
              @click="showDeleteModal = false"
              class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ยกเลิก
            </button>
            <button
              @click="proceedToVerification"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ดำเนินการต่อ
            </button>
          </div>
        </div>
      </div>

      <!-- Verification Modal -->
      <div v-if="showVerificationModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันขั้นสุดท้าย</h2>
          <p class="text-sm text-indigo-100 mb-4">
            กรุณาพิมพ์ชื่อตัวละครของคุณ (<strong>{{ characterName }}</strong>) เพื่อยืนยันการลบ
          </p>
          <input
            v-model="verificationInput"
            type="text"
            placeholder="พิมพ์ชื่อตัวละคร"
            class="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
          <p v-if="verificationError" class="text-sm text-red-400 mb-4">{{ verificationError }}</p>
          <div class="flex justify-end gap-2">
            <button
              @click="showVerificationModal = false"
              class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200"
            >
              ยกเลิก
            </button>
            <button
              @click="deleteCharacter"
              :disabled="isDeleting"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md shadow-md transition-transform duration-200 flex items-center gap-2"
            >
              <i v-if="isDeleting" class="fas fa-spinner animate-spin"></i>
              ลบตัวละคร
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { projectFunctions, projectAuth, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  setup() {
    const { user } = getUser();
    const userData = ref(null);
    const isLoading = ref(true);
    const error = ref('');
    const showDeleteModal = ref(false);
    const showVerificationModal = ref(false);
    const verificationInput = ref('');
    const verificationError = ref('');
    const isDeleting = ref(false);
    const characterName = ref('');
    const showStopPlayingModal = ref(false);
    const showStopPlayingVerificationModal = ref(false);
    const stopPlayingVerificationInput = ref('');
    const stopPlayingVerificationError = ref('');
    const isStopping = ref(false);
    const isSigningOut = ref(false);
    const isEditing = ref(false); // Added to fix the warning: default to false for display mode
    const router = useRouter();

    // Format Firestore timestamp
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate();
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        userData.value = { ...data, photoURL: user.value?.photoURL };
      } else {
        userData.value = null;
        error.value = 'กรุณาล็อกอินเพื่อดูข้อมูลบัญชี';
      }
      isLoading.value = false;
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data && data.characterName) {
        characterName.value = data.characterName;
      } else {
        characterName.value = '';
      }
    }, { immediate: true });

    // Open stop playing confirmation modal
    const openStopPlayingModal = () => {
      showStopPlayingModal.value = true;
    };

    // Proceed to stop playing verification step
    const proceedToStopPlayingVerification = () => {
      showStopPlayingModal.value = false;
      showStopPlayingVerificationModal.value = true;
    };

    // Stop playing character
    const stopPlayingCharacter = async () => {
      if (stopPlayingVerificationInput.value !== characterName.value) {
        stopPlayingVerificationError.value = 'ชื่อตัวละครไม่ถูกต้อง';
        return;
      }

      isStopping.value = true;
      try {
        // Call Firebase Function to stop playing
        const characterActions = projectFunctions.httpsCallable('gameActions');
        await characterActions({ action: 'characterActions', subAction: 'stopPlayingCharacter' });

        // Update local user data (character is now "dead", but not deleted)
        userData.value.characterCreated = true; // Still exists, but dead
        showStopPlayingVerificationModal.value = false;
        stopPlayingVerificationInput.value = '';
        stopPlayingVerificationError.value = '';
      } catch (err) {
        console.error('Error stopping playing:', err);
        stopPlayingVerificationError.value = 'เกิดข้อผิดพลาดในการหยุดเล่น: ' + err.message;
      } finally {
        isStopping.value = false;
      }
    };

    // Open delete confirmation modal
    const openDeleteModal = () => {
      showDeleteModal.value = true;
    };

    // Proceed to verification step
    const proceedToVerification = () => {
      showDeleteModal.value = false;
      showVerificationModal.value = true;
    };

    // Delete character
    const deleteCharacter = async () => {
      if (verificationInput.value !== characterName.value) {
        verificationError.value = 'ชื่อตัวละครไม่ถูกต้อง';
        return;
      }

      isDeleting.value = true;
      try {
        // Call Firebase Function to delete user data
        const characterActions = projectFunctions.httpsCallable('gameActions');
        await characterActions({ action: 'characterActions', subAction: 'deleteUserCharacter' });

        // Update local user data
        userData.value.characterCreated = false;
        characterName.value = '';
        showVerificationModal.value = false;
        verificationInput.value = '';
        verificationError.value = '';
      } catch (err) {
        console.error('Error deleting character:', err);
        verificationError.value = 'เกิดข้อผิดพลาดในการลบตัวละคร: ' + err.message;
      } finally {
        isDeleting.value = false;
      }
    };

    // Sign out
    const signOut = async () => {
      isSigningOut.value = true;
      try {
        await projectAuth.signOut();
        router.push('/');
      } catch (err) {
        console.error('Error signing out:', err);
        error.value = err.message;
      } finally {
        isSigningOut.value = false;
      }
    };


      
    return {
      userData,
      isLoading,
      error,
      formatTimestamp,
      showDeleteModal,
      showVerificationModal,
      verificationInput,
      verificationError,
      isDeleting,
      characterName,
      openDeleteModal,
      proceedToVerification,
      deleteCharacter,
      showStopPlayingModal,
      showStopPlayingVerificationModal,
      stopPlayingVerificationInput,
      stopPlayingVerificationError,
      isStopping,
      openStopPlayingModal,
      proceedToStopPlayingVerification,
      stopPlayingCharacter,
      signOut,
      isSigningOut,
      isEditing, // Return the new ref
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
</style>